import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sparkles, AlertCircle, Settings2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { API_BASE_URL } from '../config/api';
import SystemSetupPanel from './compiler/SystemSetupPanel';

const AIStoryGeneratorPanel = ({ onStoryGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Prompt Required',
        description: 'Please describe your story idea first.',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Get or create user ID
      let userId = localStorage.getItem('tweeporter_user_id');
      if (!userId) {
        userId = 'user_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('tweeporter_user_id', userId);
      }

      // Generate session ID
      const sessionId = 'session_' + Date.now();

      const response = await fetch(
        `${API_BASE_URL}/api/generate-story`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: prompt,
            user_id: userId,
            session_id: sessionId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate story');
      }

      const data = await response.json();

      // Call parent callback with generated Twee code
      onStoryGenerated(data.response);

      toast({
        title: 'Story Generated!',
        description: `AI-generated story inserted into editor. ${data.remaining_prompts} prompts remaining today.`
      });

      // Clear prompt
      setPrompt('');

    } catch (error) {
      console.error('Error generating story:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate story. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="border border-slate-300 bg-white p-5 shadow-sm" data-testid="compiler-ai-workspace">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <h3 className="font-semibold text-slate-900" data-testid="compiler-ai-workspace-title">AI Story Generator</h3>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => setSetupOpen(true)} data-testid="system-setup-trigger-button"><Settings2 className="mr-2 h-4 w-4" />System Setup</Button>
      </div>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="workflow-stage-pills">{['Draft', 'Generate', 'Review', 'Apply'].map((stage) => <span key={stage} className="border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600" data-testid={`workflow-stage-${stage.toLowerCase()}`}>{stage}</span>)}<span className="ml-auto text-xs font-medium text-emerald-700" data-testid="ai-engine-readiness">AI Engine Ready</span></div>

      <div className="mb-3">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe your story idea in plain English... 

Examples:
• 'Create a business scenario about choosing between cloud providers'
• 'Make a training story about handling customer complaints'
• 'Generate a mystery story set in an old library'"
          className="w-full h-32 p-3 border border-amber-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
          disabled={isGenerating}
          data-testid="prompt-input-textarea"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-amber-700">
          <AlertCircle className="h-4 w-4" />
          <span>Generates 3-20 passages with branching logic</span>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || !prompt.trim()}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="prompt-submit-button"
        >
          {isGenerating ? (
            <>
              <Sparkles className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Story with AI
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-amber-700 mt-3" data-testid="ai-generator-note">
        💡 Tip: The generated story will <strong>overwrite</strong> your current editor content. Copy any important code first!
      </p>
      <SystemSetupPanel open={setupOpen} onOpenChange={setSetupOpen} />
    </div>
  );
};

export default AIStoryGeneratorPanel;
